import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { PageHeader } from "@/components/layout/page-header";
import { StackTable, StackRow, StackCell } from "@/components/mercado/stack-table";
import { Delta } from "@/components/mercado/delta";
import { useAuth } from "@/lib/auth-context";
import {
  useAdminCotacoes,
  useExcluirCotacao,
  usePublicarCotacoes,
  usePracasAdmin,
} from "@/hooks/use-cotacoes";
import { fmtMoedaBR, fmtDataBR } from "@/lib/formato-mercado";
import { hojeNaFazenda } from "@/lib/data-fazenda";
import { somarDiasCivis } from "@shared/data-fazenda";
import { delta as calcularDelta } from "@shared/mercado-cotacoes";
import { PRODUTOS, chaveProduto } from "@shared/mercado-produtos";
import { ChipReferencial } from "@/components/mercado/status-chip";

/**
 * Publicar o preço do dia — a tela do RIZOMA, não da fazenda.
 *
 * ⚠️ Só usuário de PLATAFORMA (o mesmo gate de /plataforma/fazendas). A praça é
 * uma só: quem publica é o Rizoma, e não cada produtor para si. A API recusa por
 * conta própria (403); esta checagem é só para não mostrar um formulário que não
 * vai salvar.
 *
 * Sem CSV e sem gráfico. Uma linha por produto, o preço da última publicação já
 * preenchido e o delta contra ela ao lado — publicar é confirmar ou corrigir
 * quatro números, não digitar quatro números do zero.
 */

/** A semana da praça. Mesmo número do servidor (`VALIDADE_PADRAO_DIAS`), que é
 *  quem manda quando o campo vem vazio. */
const VALIDADE_PADRAO_DIAS = 6;

/** Os dois cartões da escolha. O texto de ajuda é o que evita a placa errada. */
const REFERENCIAIS = [
  {
    valor: "PRODUTOR",
    titulo: "Preço ao produtor",
    ajuda: "O que o produtor recebe por kg, no pé da carga.",
  },
  {
    valor: "VENDA",
    titulo: "Preço de venda",
    ajuda: "O preço da caixa já embalada, na mão do comprador.",
  },
] as const;

export default function AdminCotacoesPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const ehPlataforma = !!user?.plataformaAdmin;

  /**
   * Nasce VAZIA. Com uma praça ativa só, o efeito abaixo a seleciona (não há o
   * que escolher); com mais de uma e nenhuma declarada, o seletor fica sem
   * pré-seleção — chutar uma faria o admin publicar preço na praça errada sem
   * perceber. Antes isto abria em `PRACA_PADRAO` (`'irece'`), um slug que a
   * 060 apagou.
   */
  const [praca, setPraca] = useState("");
  const [data, setData] = useState(hojeNaFazenda());
  const [precos, setPrecos] = useState<Record<string, string>>({});
  /**
   * O que a placa diz. Nasce VAZIO e é o servidor quem exige (400 sem ele): um
   * default aqui carimbaria "ao produtor" numa publicação que ninguém etiquetou.
   */
  const [referencial, setReferencial] = useState("");
  /** Até quando o preço é considerado fresco. Nasce em `data + 6` — a semana da praça. */
  const [validaAte, setValidaAte] = useState(() => somarDiasCivis(hojeNaFazenda(), VALIDADE_PADRAO_DIAS));
  const [fonte, setFonte] = useState("");
  const [observacao, setObservacao] = useState("");

  const { data: pracas = [] } = usePracasAdmin(ehPlataforma);
  const ativas = pracas.filter(p => p.ativa);

  // Uma ativa só = não há escolha a fazer. Duas ou mais, o admin escolhe.
  useEffect(() => {
    if (praca || ativas.length !== 1) return;
    setPraca(ativas[0].slug);
  }, [praca, ativas.length]);
  const { data: dados, isLoading } = useAdminCotacoes(praca, data, ehPlataforma);
  const publicar = usePublicarCotacoes();
  const excluir = useExcluirCotacao();

  /**
   * Pré-preenche com o que JÁ ESTÁ publicado naquela data; na falta, com a última
   * cotação de cada produto. Roda quando a praça/data muda ou quando os dados
   * chegam — nunca sobrescreve o que já foi digitado nesta sessão de edição
   * (a dependência é a chave praça|data, não o objeto inteiro).
   */
  useEffect(() => {
    if (!dados) return;
    const base: Record<string, string> = {};
    for (const p of PRODUTOS) {
      const chave = chaveProduto(p);
      const doDia = dados.linhas.filter(l => chaveProduto(l) === chave)[0];
      const ultima = dados.ultimas.filter(l => chaveProduto(l) === chave)[0];
      const valor = doDia?.precoKg ?? ultima?.precoKg;
      base[chave] = valor != null ? valor.toFixed(2).replace(".", ",") : "";
    }
    setPrecos(base);
    const doDia = dados.linhas[0];
    setFonte(doDia?.fonte ?? "");
    setObservacao(doDia?.observacao ?? "");
    // Já publicado naquele dia: a validade que está lá. Novo: data + 6.
    setValidaAte(doDia?.validaAte ?? somarDiasCivis(dados.data, VALIDADE_PADRAO_DIAS));
    // A etiqueta do dia, se já publicou; senão a da última publicação da praça.
    // Praça sem histórico nenhum abre sem nada selecionado — e aí o admin lê a
    // placa e escolhe.
    setReferencial(dados.referencialSugerido ?? "");
  }, [dados?.praca, dados?.data, dados?.linhas.length, dados?.ultimas.length, dados?.referencialSugerido]);

  if (!ehPlataforma) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Acesso restrito ao administrador da plataforma.
      </div>
    );
  }

  function precoNumero(chave: string): number | null {
    const bruto = (precos[chave] ?? "").replace(",", ".").trim();
    if (!bruto) return null;
    const n = parseFloat(bruto);
    return isFinite(n) && n > 0 ? n : null;
  }

  function publicarTudo() {
    const linhas = PRODUTOS
      .map(p => ({ variedade: p.variedade, qualidade: p.qualidade, precoKg: precoNumero(chaveProduto(p)) }))
      .filter(l => l.precoKg != null) as Array<{ variedade: string; qualidade: string | null; precoKg: number }>;

    if (!praca) {
      toast({ title: "Escolha a praça", description: "Há mais de uma cadastrada — diga em qual está publicando.", variant: "destructive" });
      return;
    }
    if (linhas.length === 0) {
      toast({ title: "Nenhum preço preenchido", description: "Informe ao menos um produto.", variant: "destructive" });
      return;
    }
    // O servidor recusa sem referencial (400). A checagem aqui é só para não
    // mandar um request que já se sabe perdido — a regra é de lá.
    if (!referencial) {
      toast({ title: "Diga o que a praça divulgou", description: "Preço ao produtor ou preço de venda.", variant: "destructive" });
      return;
    }
    publicar.mutate(
      { praca, data, referencial, validaAte, linhas, fonte: fonte.trim() || null, observacao: observacao.trim() || null },
      {
        onSuccess: () => toast({ title: `Cotação de ${fmtDataBR(data)} publicada.`, description: `${linhas.length} ${linhas.length === 1 ? "preço" : "preços"} em ${praca}.` }),
        onError: (e: Error) => toast({ title: "Não deu para publicar", description: e.message, variant: "destructive" }),
      },
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      <PageHeader
        titulo="Cotações da praça"
        descricao="Publique o preço do dia. Uma linha por produto; o histórico fica embaixo."
        empilharQuandoEstreito
        acoesSecundarias={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={praca} onValueChange={setPraca}>
              <SelectTrigger className="w-48" data-testid="select-praca-admin"><SelectValue placeholder="Praça" /></SelectTrigger>
              <SelectContent>
                {pracas.map(p => (
                  <SelectItem key={p.slug} value={p.slug}>{p.nome} · {p.uf}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={data}
              onChange={e => setData(e.target.value)}
              className="w-44"
              data-testid="input-data-cotacao"
            />
          </div>
        }
      />

      <section className="rounded-lg border bg-card shadow-xs" data-testid="form-cotacoes">
        <header className="border-b px-3 py-2">
          <p className="text-sm font-semibold">Preço do dia · {fmtDataBR(data)}</p>
        </header>
        <div className="p-3 space-y-2">
          {/* A PRIMEIRA PERGUNTA DA TELA, e não um campo no meio do formulário.
              O mesmo 1,60 quer dizer coisas diferentes conforme a placa, e é
              esta escolha que decide a forma da comparação do produtor depois
              (% quando as bases batem, R$/kg de diferença quando não batem).
              Por isso são dois cartões grandes, e não um select. */}
          <fieldset className="space-y-1.5" data-testid="campo-referencial">
            <Label className="text-xs uppercase text-muted-foreground">O que a praça divulgou</Label>
            <div className="grid gap-2 sm:grid-cols-2">
              {REFERENCIAIS.map(op => {
                const escolhido = referencial === op.valor;
                return (
                  <button
                    key={op.valor}
                    type="button"
                    onClick={() => setReferencial(op.valor)}
                    aria-pressed={escolhido}
                    className={`rounded-lg border p-3 text-left transition-colors ${
                      escolhido ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:bg-muted/50"
                    }`}
                    data-testid={`button-referencial-${op.valor}`}
                  >
                    <span className="block text-sm font-semibold">{op.titulo}</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">{op.ajuda}</span>
                  </button>
                );
              })}
            </div>
            {/* Nada de desconto, nada de estimativa: a cotação guarda o que foi
                divulgado, com a etiqueta do que é. */}
            <p className="text-[11px] text-muted-foreground">
              A cotação guarda o preço como foi divulgado. O Rizoma não converte um no outro.
            </p>
          </fieldset>

          {PRODUTOS.map(p => {
            const chave = chaveProduto(p);
            const ultima = dados?.ultimas.filter(l => chaveProduto(l) === chave)[0];
            const atual = precoNumero(chave);
            return (
              <div key={chave} className="flex flex-wrap items-center gap-2" data-testid={`linha-produto-${p.variedade}-${p.qualidade ?? "s"}`}>
                <span className="w-24 text-sm font-medium">{p.label}</span>
                <span className="text-sm text-muted-foreground">R$/kg</span>
                <Input
                  className="w-28 text-right font-mono font-semibold"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={precos[chave] ?? ""}
                  onChange={e => setPrecos(prev => ({ ...prev, [chave]: e.target.value }))}
                  data-testid={`input-preco-${p.variedade}-${p.qualidade ?? "s"}`}
                />
                {/* O delta é contra a ÚLTIMA publicação, não contra a linha de
                    hoje — é ele que denuncia o dedo errado (1,45 → 14,50). */}
                {ultima && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Delta value={calcularDelta(atual, ultima.precoKg)} />
                    vs {fmtDataBR(ultima.data)} ({fmtMoedaBR(ultima.precoKg)})
                  </span>
                )}
              </div>
            );
          })}

          <div className="grid gap-2 sm:grid-cols-2 pt-1">
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Válida até</Label>
              <Input
                type="date"
                value={validaAte}
                onChange={e => setValidaAte(e.target.value)}
                data-testid="input-valida-ate"
              />
              {/* Vencer NÃO tira o preço do ar: a última publicada continua sendo
                  a válida. O que muda é o chip no quadro e a confirmação a mais
                  para colocar uma oferta na praça. */}
              <p className="text-[11px] text-muted-foreground mt-1">
                Depois desta data o quadro mostra "vencida" — o preço continua no ar.
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Fonte (opcional)</Label>
              <Input value={fonte} onChange={e => setFonte(e.target.value)} placeholder="pesquisa com 3 corretores" data-testid="input-fonte" />
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Observação (opcional)</Label>
              <Input value={observacao} onChange={e => setObservacao(e.target.value)} placeholder="chuva atrasou a carga da semana" data-testid="input-observacao" />
            </div>
          </div>
        </div>
        <footer className="border-t px-3 py-2 flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">
            Publicar de novo o mesmo dia <b>atualiza</b> os preços — não duplica.
          </span>
          <Button onClick={publicarTudo} disabled={publicar.isPending} data-testid="button-publicar">
            {publicar.isPending ? "Publicando…" : "Publicar"}
          </Button>
        </footer>
      </section>

      <section className="rounded-lg border bg-card shadow-xs" data-testid="historico-cotacoes">
        <header className="border-b px-3 py-2">
          <p className="text-sm font-semibold">Histórico</p>
        </header>
        <div className="p-3">
          {!praca ? (
            /* Sem praça escolhida não há histórico DE QUÊ — "nada publicado"
               aqui leria como uma praça vazia, e o que falta é a pergunta. */
            <p className="py-6 text-center text-sm text-muted-foreground" data-testid="texto-escolha-praca">
              Escolha a praça para ver o histórico.
            </p>
          ) : isLoading ? (
            <div className="h-32 animate-pulse rounded-md bg-muted" />
          ) : (dados?.historico ?? []).length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Nada publicado nesta praça ainda.</p>
          ) : (
            <StackTable
              colunas={[
                { label: "Data", w: true },
                { label: "Prata 1ª", num: true },
                { label: "2ª", num: true },
                { label: "3ª", num: true },
                { label: "Nanica", num: true },
                { label: "Divulgado como" },
                { label: "Fonte" },
                { label: "" },
              ]}
              larguraMinima={820}
              testId="tabela-historico-cotacoes"
            >
              {(dados?.historico ?? []).map(dia => (
                <StackRow key={dia.data} testId={`linha-historico-${dia.data}`}>
                  <StackCell w>{fmtDataBR(dia.data)}</StackCell>
                  {PRODUTOS.map(p => (
                    <StackCell key={chaveProduto(p)} label={p.label} num>
                      {dia.precos[chaveProduto(p)] != null ? fmtMoedaBR(dia.precos[chaveProduto(p)]) : "—"}
                    </StackCell>
                  ))}
                  <StackCell label="Divulgado como">
                    <ChipReferencial referencial={dia.referencial} testId={`chip-referencial-${dia.data}`} />
                  </StackCell>
                  <StackCell label="Fonte">{dia.fonte ?? "—"}</StackCell>
                  <StackCell acts>
                    {/* Editar/Excluir só no dia de HOJE: o histórico é a série que
                        o produtor usa para negociar, e um buraco nela ninguém
                        percebe até alguém perguntar. Corrigir dia antigo é
                        republicar naquela data (upsert). */}
                    {dia.data === hojeNaFazenda() && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => setData(dia.data)} data-testid={`button-editar-${dia.data}`}>
                          Editar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={excluir.isPending}
                          onClick={() => {
                            for (const id of dia.ids) excluir.mutate(id);
                          }}
                          data-testid={`button-excluir-${dia.data}`}
                        >
                          Excluir
                        </Button>
                      </>
                    )}
                  </StackCell>
                </StackRow>
              ))}
            </StackTable>
          )}
        </div>
      </section>
    </div>
  );
}
